import { Component, Input, Output, EventEmitter, ViewChild, ElementRef } from '@angular/core';

@Component({
    selector: 'app-paginator',
    templateUrl: './paginator.component.html',
    styleUrls: ['./paginator.component.scss']
})
export class PaginatorComponent {
    @Input() totalPages = 1;
    @Input() limit = 10;
    @Input() pageIndex = 1;
    @Input() loading = false;

    @Output() pageChange = new EventEmitter<number>();

    @ViewChild('leftInput') leftInputRef!: ElementRef<HTMLInputElement>;
    @ViewChild('rightInput') rightInputRef!: ElementRef<HTMLInputElement>;

    // Internal state
    showLeftInput = false;
    showRightInput = false;
    pageInputValue = '';

    get showLeftEllipsis(): boolean {
        return this.totalPages > 7 && this.pageIndex >= 5;
    }

    get showRightEllipsis(): boolean {
        return this.totalPages > 7 && this.pageIndex <= this.totalPages - 4;
    }

    get middlePages(): number[] {
        const total = this.totalPages;

        if (total <= 1) {
            return [];
        }

        if (total <= 7) {
            // Show all pages except first and last (they're shown separately)
            return Array.from({ length: Math.max(0, total - 2) }, (_, i) => i + 2);
        }

        // Calculate window around current page
        let start: number;
        let end: number;

        if (this.pageIndex <= 4) {
            // Near the beginning: show pages 2 to pageIndex+2
            start = 2;
            end = this.pageIndex + 2;
        } else if (this.pageIndex >= total - 3) {
            // Near the end: show pages pageIndex-2 to total-1
            start = this.pageIndex - 2;
            end = total - 1;
        } else {
            // In the middle: show 5 pages centered on current
            start = this.pageIndex - 2;
            end = this.pageIndex + 2;
        }

        return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    }

    onPageChange(page: number): void {
        if (page < 1 || page > this.totalPages || page === this.pageIndex) {
            return;
        }
        this.pageChange.emit(page);
    }

    toggleEllipsisInput(side: 'left' | 'right'): void {
        this.pageInputValue = '';
        if (side === 'left') {
            this.showLeftInput = true;
            this.showRightInput = false;
            setTimeout(() => this.leftInputRef?.nativeElement?.focus());
        } else {
            this.showRightInput = true;
            this.showLeftInput = false;
            setTimeout(() => this.rightInputRef?.nativeElement?.focus());
        }
    }

    onPageInputKeydown(event: KeyboardEvent): void {
        if (event.key === 'Enter') {
            this.goToInputPage();
        } else if (event.key === 'Escape') {
            this.closePageInputs();
        }
    }

    goToInputPage(): void {
        const page = parseInt(this.pageInputValue, 10);
        if (!isNaN(page)) {
            // Clamp page to valid range [1, totalPages]
            const clampedPage = Math.max(1, Math.min(page, this.totalPages));
            this.closePageInputs();
            this.onPageChange(clampedPage);
        }
    }

    closePageInputs(): void {
        this.showLeftInput = false;
        this.showRightInput = false;
        this.pageInputValue = '';
    }
}
